import React, { useState } from "react";

import { FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IconButton, InputAdornment } from "@mui/material";

import { LockerIcon } from "@/assets/icons";
import { TextInputProps } from "@/components/atoms/Form/TextInput/TextInput";
import { StyledEyeIcon, StyledEyeSplashIcon } from "@/styles/components";

import { BorderLinearProgress, StyledTextInput } from "./styled";
import { calculatePasswordStrength } from "./utils";

export type PasswordInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<TextInputProps<TFieldValues, TName>, "type"> & {
    isNew?: boolean;
    hidePasswordIcon?: boolean;
    hideProgressBar?: boolean;
};

const PasswordInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    isNew,
    hidePasswordIcon = false,
    hideProgressBar = false,
    ...textFieldProps
}: PasswordInputProps<TFieldValues, TName>): React.ReactElement => {
    const { t } = useTranslation();
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const toggleShowPassword = () => setShowPassword(!showPassword);

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setPassword(newValue);
        if (textFieldProps.onChange) {
            textFieldProps.onChange(event);
        }
    };
    if (!textFieldProps.helperText && isNew && !hideProgressBar) {
        textFieldProps.helperText = t("Validations.PasswordComplexity");
    }

    return (
        <>
            <StyledTextInput
                autoComplete={isNew ? "new-password" : "current-password"}
                {...textFieldProps}
                value={password}
                onChange={handlePasswordChange}
                type={showPassword ? "text" : "password"}
                InputProps={{
                    ...textFieldProps.InputProps,
                    startAdornment: !hidePasswordIcon && (
                        <InputAdornment position="start">
                            <LockerIcon />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={toggleShowPassword}>
                                {showPassword ? (
                                    <StyledEyeSplashIcon alt="password" />
                                ) : (
                                    <StyledEyeIcon alt="password" />
                                )}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            {isNew && !hideProgressBar && (
                <BorderLinearProgress
                    variant="determinate"
                    value={calculatePasswordStrength(password)}
                    {...(textFieldProps.disabled !== undefined ? { disabled: textFieldProps.disabled } : {})}
                />
            )}
        </>
    );
};

export default PasswordInput;
